// AI Generated Test
import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { QAImportantHighlightsService } from '@client/app/blocks/service/qa/important-highlights.service';
import { StateService } from '@uirouter/core';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { SpellingPagesDialogComponent } from '@client/app/pages/qa/spellcheck/pages/spelling-pages-dialog.component';
import { LinkContentComponent } from '@client/app/pages/qa/broken-links/links/link-content/link-content.component';

@Component({
    selector: 'important-highlights',
    standalone: false,
    template: `
        <div class="card panel-monsido-card">
            <mon-panel-header [pHeader]="'Important Highlights' | translate"></mon-panel-header>
            <div class="card-body">
                <mon-spinner [progress]="progress">
                    <table class="table table-hover remove-border-on-first-row table-vertial-align table-fixed">
                        <tbody>
                        <tr *ngIf="spellingErrors.length && showSpelling" [attr.aria-label]="'Misspelling affecting the most content' | translate">
                            <td>
                                <div class="d-flex align-items-center"
                                     tabindex="0"
                                     role="button"
                                     (click)="openDialog('spellingPagesDialog', 'lg', {word: spellingErrors[0], pageDetailTarget: 'misspellings'})">
                                    <icon-circle class="px-2" color="text-link" icon="fas fa-language"
                                                 [monAriaText]="'Misspelling' | translate"></icon-circle>
                                    <div class="text-ellipsis">
                                        <p class="mb-1 text-ellipsis"><span translate>Misspelling affecting the most content</span>:</p>
                                        <div class='label label-danger inner-b-smurf inner-t-smurf'>{{spellingErrors[0].word}}</div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr *ngIf="data && data.pages_with_potential_misspellings > 0 && showSpelling" [attr.aria-label]="'Potential misspellings' | translate">
                            <td>
                                <div class="d-flex align-items-center"
                                     (click)="goTo('base.customer.domain.qa.spellcheck.potential')"
                                     tabindex="0"
                                     role="button">
                                    <icon-circle class="px-2" color="text-link" icon="fas fa-info"
                                                 [monAriaText]="'Potential misspelling' | translate"></icon-circle>
                                    <div class="text-ellipsis">
                                        <div class='label label-info inner-b-smurf inner-t-smurf'>{{ data.potential_misspellings | monNumber }}</div>&nbsp;
                                        <span translate>Potential misspellings</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr *ngIf="showBrokenLink" [attr.aria-label]="'Broken link affecting most pages' | translate">
                            <td>
                                <div class="d-flex align-items-center"
                                     tabindex="0"
                                     role="button"
                                     (click)="openDialog('linkContent', 'md', {link: brokenLink, type: brokenLinkType})">
                                    <icon-circle class="px-2" color="text-link" icon="fas fa-unlink"
                                                 [monAriaText]="'Broken links' | translate"></icon-circle>
                                    <div class="text-ellipsis">
                                        <p class="mb-1 text-ellipsis">{{brokenLinkText}}:</p>
                                        <div class='label label-danger inner-b-smurf inner-t-smurf'>{{ brokenLink.url }}</div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr *ngIf="showBrokenImage" [attr.aria-label]="'Broken image affecting most pages' | translate">
                            <td>
                                <div class="d-flex align-items-center"
                                     tabindex="0"
                                     role="button"
                                     (click)="openDialog('linkContent', 'md', {link: brokenImage, type: brokenLinkType})">
                                    <icon-circle class="px-2" color="text-link" icon="fas fa-image"
                                                 [monAriaText]="'Broken images' | translate"></icon-circle>
                                    <div class="text-ellipsis">
                                        <p class="mb-1 text-ellipsis">{{brokenImageText}}:</p>
                                        <span class='label label-danger inner-b-smurf inner-t-smurf'>{{ brokenImage.url }}</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class ImportantHighlightsComponent implements OnInit, OnChanges {
    @Input() progress: any;
    @Input() data: any;
    @Input() spellingErrors: any;
    @Input() brokenLink: any;
    @Input() brokenImage: any;

    showSpelling: any;
    showBrokenLink: any;
    showBrokenImage: any;
    brokenLinkText: any;
    brokenImageText: any;
    brokenLinkType: any;

    constructor (
        private monEventService: MonEventService,
        private qaImportantHighlightsService: QAImportantHighlightsService,
        private stateService: StateService,
        private activeFeatureService: ActiveFeatureService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnChanges (): void {
        this.update();
    }

    private activate (): void {
        this.brokenLinkText = this.qaImportantHighlightsService.getBrokenLinkText();
        this.brokenImageText = this.qaImportantHighlightsService.getBrokenImageText();
        this.brokenLinkType = this.qaImportantHighlightsService.getLinkSearchType();
        this.showSpelling = this.activeFeatureService.isFeatureActivePure('qa_spellcheck');
        this.showBrokenLink = this.qaImportantHighlightsService.showBrokenLink(this.brokenLink);
        this.showBrokenImage = this.qaImportantHighlightsService.showBrokenImage(this.brokenImage);
    }

    private update (): void {
        this.showBrokenLink = this.qaImportantHighlightsService.showBrokenLink(this.brokenLink);
        this.showBrokenImage = this.qaImportantHighlightsService.showBrokenImage(this.brokenImage);
    }

    openDialog (target: string, size: any, attrs?: any): void {
        if (size.constructor === Object) {
            attrs = size;
            size = 'fw';
        }

        let component: any;

        switch (target) {
            case 'linkContent':
                component = LinkContentComponent;
                break;
            case 'spellingPagesDialog':
                component = SpellingPagesDialogComponent;
                break;
        }

        const params = {
            body: component,
            size: size,
            data: attrs,
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    goTo (state: any): void {
        this.monUIRouterTransitionService.onTransitionFinished(() => {
            this.stateService.go(state);
        });
    }
}
